import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ContactForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) {
      toast.error("Please enter your name.");
      return;
    }
    if (!emailRegex.test(trimmedEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: trimmedName,
        email: trimmedEmail,
      });
      setSubmitted(true);
      toast.success("You're on the list. We'll be in touch.");
      setName("");
      setEmail("");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="border border-white/15 bg-white/[0.02] px-6 py-8"
        data-testid="contact-success-state"
      >
        <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
          Confirmed
        </div>
        <p className="font-serif-display text-white/90 text-2xl md:text-3xl leading-snug">
          Thank you. You'll hear from us
          <br className="hidden sm:block" /> when the doors open.
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="mt-6 font-mono-plex text-[11px] uppercase tracking-[0.28em] text-white/50 hover:text-white/90 transition-colors"
          data-testid="contact-reset-button"
        >
          ← Submit another
        </button>
      </div>
    );
  }

  return (
    <div>
      <div
        className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4"
        data-testid="contact-form-label"
      >
        Request early access
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:border sm:border-white/15 sm:bg-white/[0.02] sm:divide-x sm:divide-white/10"
        data-testid="contact-form"
        noValidate
      >
        <Input
          type="text"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={loading}
          data-testid="contact-name-input"
          aria-label="Your name"
          className="rounded-none border border-white/15 sm:border-0 bg-white/[0.02] sm:bg-transparent text-white placeholder:text-white/30 font-mono-plex text-sm tracking-wide h-12 px-4 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/50 flex-1 min-w-0"
        />
        <Input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          data-testid="contact-email-input"
          aria-label="Your email"
          className="rounded-none border border-white/15 sm:border-0 bg-white/[0.02] sm:bg-transparent text-white placeholder:text-white/30 font-mono-plex text-sm tracking-wide h-12 px-4 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/50 flex-1 min-w-0"
        />
        <Button
          type="submit"
          disabled={loading}
          data-testid="contact-submit-button"
          className="rounded-none h-12 px-6 bg-white text-black hover:bg-white/85 font-mono-plex text-[11px] uppercase tracking-[0.24em] font-medium inline-flex items-center gap-2 shrink-0 disabled:opacity-70"
        >
          {loading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Sending</span>
            </>
          ) : (
            <>
              <span>Notify Me</span>
              <ArrowUpRight className="h-3.5 w-3.5" />
            </>
          )}
        </Button>
      </form>

      <p
        className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-white/30 mt-4"
        data-testid="contact-form-legal"
      >
        No spam. Ever. Unsubscribe anytime.
      </p>
    </div>
  );
};

export default ContactForm;
